/* eslint-disable @typescript-eslint/naming-convention */
import type { GetError } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";

export const containsProhibitedContent = (text: string): boolean => {
  if (!text || text.trim() === "") {
    return false;
  }

  const htmlTagPattern = /<\/?[a-z][\s\S]*>/i;
  const imagePattern = /<img[\s\S]*?>|!\[.*?\]\(.*?\)/i;
  const linkPattern = /<a[\s\S]*?>|https?:\/\/[^\s]+|\[.*?\]\(.*?\)/i;
  const javascriptPattern = /<script[\s\S]*?>|on\w+\s*=|javascript:/i;
  const styleUrlPattern = /style\s*=\s*["'][^"']*url\s*\([^)]*\)/i;
  const dataUriPattern = /data:image\//i;

  return (
    htmlTagPattern.test(text) ||
    imagePattern.test(text) ||
    linkPattern.test(text) ||
    javascriptPattern.test(text) ||
    styleUrlPattern.test(text) ||
    dataUriPattern.test(text)
  );
};

export function prohibitedContentValidation(
  label: string,
  value: string,
  changeError = (id: string, status: boolean) => {
    /* no-op */
  }
): GetError {
  return (_label: string, value: string) => {
    if (containsProhibitedContent(value)) {
      changeError(label, true);
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: Locale.getText("PROHIBITED_CONTENT_ERROR_MESSAGE") as string
        }
      };
    }
    changeError(label, false);
    return {
      triggered: false,
      validationState: {
        isValid: true,
        type: "success"
      }
    };
  };
}

export function requiredValidation(
  label: string,
  value: string,
  changeError = (id: string, status: boolean) => {
    /* no-op */
  }
): GetError {
  return (_label: string, value: string) => {
    const v = value?.trim() || "";
    if (v.length === 0) {
      changeError(label, true);
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: Locale.getText("FIELD_REQUIRED") as string
        }
      };
    }
    changeError(label, false);
    return {
      triggered: false,
      validationState: {
        isValid: true,
        type: "success"
      }
    };
  };
}

export function percentValidation(
  label: string,
  value: string,
  isRequired = false,
  changeError = (id: string, status: boolean) => {
    /* no-op */
  }
): GetError {
  return (_label: string, value: string) => {
    const v = value?.trim() || "";
    if (isRequired && v.length === 0) {
      changeError(label, true);
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: Locale.getText("FIELD_REQUIRED") as string
        }
      };
    }
    const num = Number(v);
    if (isNaN(num) || num < 0 || num > 100) {
      changeError(label, true);
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: Locale.getText("FIELD_PERCENTAGE") as string
        }
      };
    }
    changeError(label, false);
    return {
      triggered: false,
      validationState: {
        isValid: true,
        type: "success"
      }
    };
  };
}

export function integerValidation(
  label: string,
  value: string,
  isRequired = false,
  changeError = (id: string, status: boolean) => {
    /* no-op */
  }
): GetError {
  return (_label: string, value: string) => {
    const v = value?.trim() || "";
    if (isRequired && v.length === 0) {
      changeError(label, true);
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: Locale.getText("FIELD_REQUIRED") as string
        }
      };
    }
    const num = Number(v);
    if (isNaN(num) || num < 0 || !Number.isInteger(num)) {
      changeError(label, true);
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: Locale.getText("FIELD_INTEGER") as string
        }
      };
    }
    changeError(label, false);
    return {
      triggered: false,
      validationState: {
        isValid: true,
        type: "success"
      }
    };
  };
}

export function cashValidation(
  label: string,
  value: string,
  isRequired = false,
  changeError = (id: string, status: boolean) => {
    /* no-op */
  }
): GetError {
  return (_label: string, value: string) => {
    const v = value?.trim() || "";
    if (isRequired && v.length === 0) {
      changeError(label, true);
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: Locale.getText("FIELD_REQUIRED") as string
        }
      };
    }
    const num = Number(v);
    if (isNaN(num) || num < 0) {
      changeError(label, true);
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: Locale.getText("FIELD_INVALID") as string
        }
      };
    }
    changeError(label, false);
    return {
      triggered: false,
      validationState: {
        isValid: true,
        type: "success"
      }
    };
  };
}

export function dateInputValidation(value: number): boolean {
  if (!value || isNaN(value) || value <= 0) return true;
  const date = new Date(value);
  return isNaN(date.getTime());
}

export function dateRangeValidation(startDate: number, endDate: number) {
  const startDateError = dateInputValidation(startDate);
  let endDateError = dateInputValidation(endDate);
  let endDatePastError = false;

  if (!endDateError && endDate) {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const endDateObj = new Date(endDate);
    endDateObj.setHours(0, 0, 0, 0);

    if (endDateObj < currentDate) {
      endDateError = true;
      endDatePastError = true;
    }
  }

  if (startDateError || endDateError) {
    return {
      startDateError,
      endDateError,
      endDatePastError,
      rangeError: false
    };
  }

  const startDateObj = new Date(startDate);
  const endDateObj = new Date(endDate);
  startDateObj.setHours(0, 0, 0, 0);
  endDateObj.setHours(0, 0, 0, 0);
  const rangeError = startDateObj > endDateObj;

  return {
    startDateError: false,
    endDateError: false,
    endDatePastError: false,
    rangeError
  };
}

export function combineValidations(...validations: GetError[]): GetError {
  return (label: string, value: string) => {
    for (const validation of validations) {
      const result = validation(label, value);
      if (result.triggered && !result.validationState.isValid) {
        return result;
      }
    }
    return {
      triggered: false,
      validationState: {
        isValid: true,
        type: "success"
      }
    };
  };
}
